// 공정 표준 보정(#171) — 기능공 수·작업량 계수 · 비우면 공정 표준 기능공 수·계수 1 · 보통인부는 표준 그대로 · 저장은 대표만.

import { FormEvent, useState, useTransition } from "react";
import { toast } from "sonner";

import SaveBar from "@/components/save-bar";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import SectionCard from "@buildpy/ui/section-card";
import { PROCESS_CREW_MAX, PROCESS_FACTOR_MAX, PROCESS_FACTOR_MIN } from "@buildpy/core/modules/process-standard/types";
import { settleRedirect } from "@/lib/redirect-error";
import { saveProcessCalibration } from "@/api/actions/org";

interface Props {
  canEdit: boolean;
  crewSize: number | null;
  factor: number | null;
}

// 빈 칸은 null(되돌리기) · 나머지는 숫자로 보내 검사는 액션 한 벌에 맡긴다.
const numberOrNull = (value: string): number | null => (value.trim() === "" ? null : Number(value.trim()));

const SettingsProcessCalibration = ({ canEdit, crewSize, factor }: Props) => {
  const [crew, setCrew] = useState(crewSize === null ? "" : String(crewSize));
  const [rate, setRate] = useState(factor === null ? "" : String(factor));
  const [dirty, setDirty] = useState(false);
  const [pending, startTransition] = useTransition();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    startTransition(async () => {
      try {
        const result = await saveProcessCalibration({ crew_size: numberOrNull(crew), factor: numberOrNull(rate) });
        if (result.ok) {
          setDirty(false);
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } catch (caught) {
        if (settleRedirect(caught)) return;
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <SectionCard
      as="form"
      data-testid="settings-process-calibration"
      description="시공일정 초안의 공정 일수를 회사 사정에 맞춥니다. 비우면 공정 표준 값을 씁니다."
      onSubmit={onSubmit}
      title="공정 표준 보정">
      <fieldset className="grid gap-4 md:grid-cols-2" disabled={!canEdit || pending}>
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="process_crew_size" id="process_crew_size-label">
            기능공 수 (명)
          </Label>
          <Input
            aria-labelledby="process_crew_size-label"
            id="process_crew_size"
            inputMode="numeric"
            max={PROCESS_CREW_MAX}
            min={1}
            name="process_crew_size"
            onChange={(e) => {
              setCrew(e.target.value);
              setDirty(true);
            }}
            placeholder="공정 표준 기능공 수"
            step={1}
            type="number"
            value={crew}
          />
          <span className="text-xs text-muted-foreground">{`1~${PROCESS_CREW_MAX}명 · 비우면 공정 표준 기능공 수`}</span>
          <span className="text-xs text-muted-foreground">보통인부는 공정 표준 인원 그대로 더합니다.</span>
        </div>

        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="process_factor" id="process_factor-label">
            작업량 계수
          </Label>
          <Input
            aria-labelledby="process_factor-label"
            id="process_factor"
            inputMode="decimal"
            max={PROCESS_FACTOR_MAX}
            min={PROCESS_FACTOR_MIN}
            name="process_factor"
            onChange={(e) => {
              setRate(e.target.value);
              setDirty(true);
            }}
            placeholder="1"
            step="0.05"
            type="number"
            value={rate}
          />
          <span className="text-xs text-muted-foreground">
            {`${PROCESS_FACTOR_MIN}~${PROCESS_FACTOR_MAX} · 비우면 1 · 1보다 크면 일수가 늘어납니다`}
          </span>
        </div>
      </fieldset>

      {canEdit ? (
        <SaveBar dirty={dirty} pending={pending} />
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">대표만 설정을 바꿀 수 있습니다.</p>
      )}
    </SectionCard>
  );
};

export default SettingsProcessCalibration;
